<script lang="ts" module>
  export type ButtonGroupItemProps = BaseProps<"button"> & {
    value: NonNullable<string | number>
    id?: string | number
    disabled?: boolean
    class?: string
    onchange?: (value: any) => void
  }
</script>

<script lang="ts">
  import { Directions, Orientations } from "#lib/internal/constants"
  import {
    generateId,
    getUiclContext,
    useActions,
    useCollection,
  } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { tick, type Snippet } from "svelte"
  import {
    getButtonGroupItemCollection,
    getButtonGroupRootContext,
  } from "./ButtonGroupRoot.svelte"
  import { buttonGroupItemVariants } from "./variants"

  type Props = ButtonGroupItemProps & { children?: Snippet }

  let {
    class: className = "",
    value = "",
    id = generateId("button-group-item"),
    disabled = false,
    use = [],
    onchange,
    children,
    ...restProps
  }: Props = $props()

  const ctx = getButtonGroupRootContext()
  const itemCollection = getButtonGroupItemCollection()

  const pressed = $derived(
    Array.isArray($ctx.value)
      ? $ctx.value.includes(value)
      : $ctx.value === value
  )

  let wasClicked = false

  function normalize(val: unknown) {
    return typeof val === "string" ? val : JSON.stringify(val)
  }

  function getTabIndex(
    items: typeof $itemCollection,
    selected: typeof $ctx.value
  ) {
    if (!items.length) return -1

    const firstValue = items[0].dataset.value
    const selectedValue = Array.isArray(selected) ? selected[0] : selected
    const target = selectedValue ?? firstValue

    return normalize(target) === normalize(value) ? 0 : -1
  }

  function handleFocus() {
    if (
      $ctx.type === "multiple" ||
      $ctx.value === null ||
      $ctx.value === undefined
    ) {
      return
    }
    if (!wasClicked) {
      handleChange()
    }
    wasClicked = false
  }

  async function toggleButtons(target: HTMLElement) {
    if ($ctx.value === null || $ctx.value === undefined) {
      target.click()
    } else {
      const otherButton = $itemCollection.find((item) => item !== target)!
      otherButton.focus()
      await tick()
      otherButton.click()
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if ($ctx.type === "multiple") return
    if (event.key === "Enter" || event.key === " ") {
      // toggle button if there are only two items
      if ($itemCollection.length === 2) {
        event.preventDefault()
        event.stopPropagation()
        toggleButtons(event.target as HTMLElement)
      }
    }
  }

  // order of operations is mouseDown -> focus -> click
  // if we don't note the state before focus, we will end up
  // toggling the value twice
  function handleMouseDown() {
    wasClicked = true
  }

  function handleClick() {
    if (pressed && $ctx.deselect && $ctx.type === "single") {
      $ctx.value = null
    } else {
      handleChange()
    }
  }

  function handleChange() {
    if (pressed && $ctx.deselect && $ctx.type === "single") {
      $ctx.value = null
    } else if ($ctx.type === "single") {
      $ctx.value = value
    } else if ($ctx.type === "multiple") {
      const values = Array.isArray($ctx.value) ? $ctx.value : []
      $ctx.value = !$ctx.value.includes(value)
        ? [...values, value]
        : values.filter((v) => v !== value)
    }
    onchange?.($ctx.value)
  }

  const v2 = !!getUiclContext()?.v2
</script>

<button
  class={cn(buttonGroupItemVariants({ className, variant: $ctx.variant, v2 }), {
    "first:rounded-l-md last:rounded-r-md":
      $ctx.orientation === Orientations.HORIZONTAL &&
      $ctx.dir === Directions.LTR,
    "first:rounded-r-md last:rounded-l-md":
      $ctx.orientation === Orientations.HORIZONTAL &&
      $ctx.dir === Directions.RTL,
    "first:rounded-t-md last:rounded-b-md":
      $ctx.orientation === Orientations.VERTICAL,
  })}
  {value}
  {id}
  type="button"
  disabled={$ctx.disabled || disabled ? true : undefined}
  aria-pressed={pressed}
  data-state={pressed ? "on" : "off"}
  data-orientation={$ctx.orientation}
  data-value={value}
  tabindex={getTabIndex($itemCollection, $ctx.value)}
  onmousedown={handleMouseDown}
  onclick={handleClick}
  onfocus={handleFocus}
  onkeydown={handleKeyDown}
  use:useActions={[
    ...(use ?? []),
    [useCollection, { collection: itemCollection }],
  ]}
  {...restProps}
>
  {@render children?.()}
</button>

{#if $ctx.name}
  <input
    type={$ctx.type === "single" ? "radio" : "checkbox"}
    hidden
    inert
    tabIndex={-1}
    name={$ctx.name}
    {value}
    {disabled}
  />
{/if}
